import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Alert,
  Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SecurityIcon from '@mui/icons-material/Security';
import { UserPrivacy, TagPrivacyField } from '../../types/interfaces';
import PrivacyFieldDisplay from './PrivacyField';

interface PrivacyOverviewProps {
  privacy: UserPrivacy;
  onUpdate: (updatedPrivacy: UserPrivacy) => void;
  readonly?: boolean;
}

const TagPrivacyDisplay: React.FC<{
  tagField: TagPrivacyField;
  onUpdate: (updatedField: TagPrivacyField) => void;
  readonly?: boolean;
}> = ({ tagField, onUpdate, readonly = false }) => {
  const [expanded, setExpanded] = useState(false);

  const tagSpecificCount = tagField.tagSpecific ? Object.keys(tagField.tagSpecific).length : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PrivacyFieldDisplay
          label="Tags (Global)"
          field={tagField}
          onUpdate={(updatedField) => onUpdate({ ...tagField, ...updatedField })}
          readonly={readonly}
        />
        {tagSpecificCount > 0 && (
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ ml: 3, mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Tag-specific overrides: {tagSpecificCount}
          </Typography>
          {tagField.tagSpecific && Object.entries(tagField.tagSpecific).map(([tagName, tagSetting]) => (
            <Box key={tagName} sx={{ ml: 2, mb: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                "{tagName}":
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tagSetting.allowedUsers.length} user override(s)
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const PrivacyOverview: React.FC<PrivacyOverviewProps> = ({ 
  privacy, 
  onUpdate, 
  readonly = false 
}) => {
  const [expanded, setExpanded] = useState(false);

  const updateField = (fieldName: keyof UserPrivacy, updatedField: any) => {
    onUpdate({
      ...privacy,
      [fieldName]: updatedField
    });
  };

  if (!privacy) {
    return (
      <Alert severity="warning">
        No privacy data available for this user. They may be using the legacy privacy system.
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SecurityIcon color="primary" />
          <Typography variant="h6">Privacy Settings</Typography>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {!expanded && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Click to expand privacy details. Global privacy levels:
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(privacy).map(([key, field]) => (
                <Typography key={key} variant="caption" sx={{ 
                  px: 1, 
                  py: 0.5, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1 
                }}>
                  {key}: {field.globalPrivacy}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        <Collapse in={expanded}>
          <Grid container spacing={2}>
            {/* Profile Fields */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Profile Fields
              </Typography>
              <PrivacyFieldDisplay
                label="Display Name"
                field={privacy.displayName}
                onUpdate={(field) => updateField('displayName', field)}
                readonly={readonly}
              />
              <PrivacyFieldDisplay
                label="Bio"
                field={privacy.bio}
                onUpdate={(field) => updateField('bio', field)}
                readonly={readonly}
              />
              <PrivacyFieldDisplay
                label="Face Picture"
                field={privacy.facePic}
                onUpdate={(field) => updateField('facePic', field)}
                readonly={readonly}
              />
            </Grid>

            {/* Identity Fields */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Identity Fields
              </Typography>
              <PrivacyFieldDisplay
                label="Birthday/Age"
                field={privacy.birthday}
                onUpdate={(field) => updateField('birthday', field)}
                readonly={readonly}
              />
              <PrivacyFieldDisplay
                label="Gender"
                field={privacy.gender}
                onUpdate={(field) => updateField('gender', field)}
                readonly={readonly}
              />
              <PrivacyFieldDisplay
                label="Race"
                field={privacy.race}
                onUpdate={(field) => updateField('race', field)}
                readonly={readonly}
              />
              <PrivacyFieldDisplay
                label="Sexual Orientation"
                field={privacy.sexualOrientation}
                onUpdate={(field) => updateField('sexualOrientation', field)}
                readonly={readonly}
              />
            </Grid>

            {/* Location & Tags */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Location & Tags
              </Typography>
              <PrivacyFieldDisplay
                label="Home Neighborhood"
                field={privacy.homeNeighborhood}
                onUpdate={(field) => updateField('homeNeighborhood', field)}
                readonly={readonly}
              />
              <TagPrivacyDisplay
                tagField={privacy.tags}
                onUpdate={(field) => updateField('tags', field)}
                readonly={readonly}
              />
            </Grid>
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default PrivacyOverview;
